export type OutboundEventType =
  | 'opened'
  | 'method_selected'
  | 'detected'
  | 'settled'
  | 'cancelled'
  | 'expired'
  | 'closed';

/**
 * What the host page receives via `c.on(...)`.
 *
 * Names match the rest of the platform (chain_id / asset_id) so an
 * integrator handling AMQP events is not learning a second vocabulary.
 *
 * There is deliberately no `tx_hash`: the widget never sees a chain hash,
 * only NeoWallet's own transaction id. Emitting that under the name
 * `tx_hash` would send an integrator to a block explorer with an id that
 * is not on any chain.
 *
 * Presentation only. `settled` here is a browser message and must never
 * credit a balance — that is the AMQP tx.settled event's job.
 */
export interface OutboundEvent {
  type: OutboundEventType;
  session_id: string;
  chain_id?: string;
  asset_id?: string;
  /** NeoWallet's transaction id — quote it to support, not to an explorer. */
  transaction_id?: string;
  /** Integer in base units, as a string. */
  expected_amount?: string;
  /** Symbol for display, when the asset is known. */
  asset_symbol?: string;
}

export type InboundMessageType = 'hello' | 'close';

export interface InboundMessage {
  type: InboundMessageType;
  origin?: string;
}

let parentOrigin: string | null = null;
const buffered: OutboundEvent[] = [];
const closeListeners = new Set<() => void>();
const BUFFER_MAX = 32;
const HELLO_TIMEOUT_MS = 5000;

function isValidOrigin(o: string): boolean {
  return /^https?:\/\/[^/]+$/.test(o);
}

let resolveHandshake!: () => void;
const handshake: Promise<void> = new Promise((resolve) => {
  resolveHandshake = resolve;
});
let handshakeSettled = false;

function settleHandshake(): void {
  if (handshakeSettled) return;
  handshakeSettled = true;
  resolveHandshake();
}

// Registered at module load so the launcher's `hello` (fired on iframe
// `load`) can't race the React mount that used to call init() from a
// useEffect. This module is imported by main.tsx, so evaluation happens
// during the initial script parse — well before iframe onload fires.
window.addEventListener('message', (event: MessageEvent) => {
  const data = event.data as InboundMessage | undefined;
  if (!data || typeof data !== 'object' || typeof data.type !== 'string') {
    return;
  }

  if (data.type === 'hello') {
    if (parentOrigin !== null) return;
    if (event.source !== window.parent) return;
    if (!isValidOrigin(event.origin)) return;
    parentOrigin = event.origin;
    for (const ev of buffered) window.parent.postMessage(ev, parentOrigin);
    buffered.length = 0;
    settleHandshake();
    return;
  }

  if (event.origin !== parentOrigin) return;

  if (data.type === 'close') {
    for (const l of closeListeners) l();
  }
});

window.setTimeout(() => {
  if (parentOrigin === null) {
    console.warn('checkout: no hello from parent after 5s');
  }
  settleHandshake();
}, HELLO_TIMEOUT_MS);

export function init(): Promise<void> {
  return handshake;
}

export function emit(event: OutboundEvent): void {
  if (parentOrigin) {
    window.parent.postMessage(event, parentOrigin);
    return;
  }
  if (buffered.length < BUFFER_MAX) {
    buffered.push(event);
  }
  if (event.type === 'closed' && !parentOrigin) {
    // Last-ditch teardown signal — parent may not have handshaken but
    // still needs to remove the iframe. Only 'closed' gets the wildcard.
    window.parent.postMessage(event, '*');
  }
}

export function onClose(listener: () => void): () => void {
  closeListeners.add(listener);
  return () => closeListeners.delete(listener);
}

export function getParentOrigin(): string | null {
  return parentOrigin;
}
